import { getProductRatingAggregate, getReviews } from "@api/rating"
import { Star } from "@components/star"
import { TProduct, TRatingAggregate, TReview } from "@components/types"
import { getSafeUrl, suggestColor } from "@core/utils"
import { Star as FaRegStar } from 'lucide-react'
import Link from "next/link"
import { useEffect, useState } from "react"


interface Props {
    isLoading?: boolean
    product: TProduct
}

export const RatingWidget = ({ isLoading, product }: Props) => {
    const [reviews, setReviews] = useState < TReview[] > ([]);
    const [ratingAggregate, setRatingAggregate] = useState < TRatingAggregate > ();
    const [ratings, setRatings] = useState < RatingDist | null > ();

    async function fetchRatingAggregates() {
        try {
            const data: TRatingAggregate = await getProductRatingAggregate(product.id);
            setRatingAggregate(data);
            setRatings({
                '5': data?.rating_dist[5] ? data?.rating_dist[5] : 0,
                '4': data?.rating_dist[4] ? data?.rating_dist[4] : 0,
                '3': data?.rating_dist[3] ? data?.rating_dist[3] : 0,
                '2': data?.rating_dist[2] ? data?.rating_dist[2] : 0,
                '1': data?.rating_dist[1] ? data?.rating_dist[1] : 0,
            })
        } catch (error) {
            console.debug(error)
        }
    }

    useEffect(() => {
        if (product?.id > 0) {
            getReviews(product?.id, 3).then((res: TReview[]) => {
                setReviews(res)
            })
        }
    }, [product?.id])

    useEffect(() => {
        fetchRatingAggregates();
    }, []);

    return (
        <div className="flex flex-col gap-2.5 font-manrope">
            <h3 className="text-lg lt-sm:text-base text-neutral-700 font-bold">
                Ratings & Reviews
            </h3>
            <div className="flex flex-row justify-between md:justify-start items-center gap-2 md:gap-8">
                <div className="text-center md:text-start flex flex-col gap-2">
                    <div className={`${suggestColor(ratingAggregate?.rating_overall, true)} flex items-center gap-2 bg-transparent`}>
                        <span className='text-3xl md:text-4xl font-bold'>
                            {ratingAggregate?.rating_overall.toFixed(2)}
                        </span>
                        <FaRegStar className='w-6 h-6 md:w-8 md:h-8' />
                    </div>
                    <p className="text-base text-neutral-600 font-medium">
                        {ratingAggregate?.rating_count} Ratings
                        <br />
                        {reviews?.length} Reviews
                    </p>
                </div>
                <div className='w-0 h-36 border border-t-none border-r-none border-b-none border-solid border-neutral-200' />
                <div className="flex flex-col gap-2.5">
                    {ratings && (
                        Object.entries(ratings).reverse().map(([rating, count]) => (
                            <div key={rating} className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                    <span className="text-xs md:text-sm text-neutral-600 font-medium">{rating}</span>
                                    <FaRegStar className="w-3 md:w-4 h-3 md:h-4 text-neutral-600" />
                                </div>
                                <div className="w-48 md:w-60 h-1 relative bg-neutral-200">
                                    <div
                                        style={{ width: `${(count / ratingAggregate?.rating_count) * 100}%` }}
                                        className={`h-full absolute top-0 left-0 ${suggestColor(Number(rating))}`}
                                    />
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-4">
                {reviews?.map((review: TReview) => {
                    return (
                        <div className="flex flex-row items-center gap-4 py-4 border border-t-none border-r-none border-l-none border-solid border-neutral-200">
                            <Star rating={review?.rating} />
                            <div>
                                <p className="text-neutral-700 text-sm">{review?.comment}</p>
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className="mt-2.5">
                {product?.stats?.rating_count <= 3 ?
                    <Link href={`/product/${product?.id}/reviews/${getSafeUrl(product?.name)}`} prefetch={false}>
                        <button type="button" className="text-base text-brand-500 font-medium cursor-pointer">
                            Add Product Review
                        </button>
                    </Link>
                    :
                    <Link href={`/product/${product?.id}/reviews/${getSafeUrl(product?.name)}`} prefetch={false}>
                        <button type="button" className="text-base text-brand-500 font-medium cursor-pointer">
                            All {product?.stats?.rating_count} Reviews
                        </button>
                    </Link>
                }
            </div>
        </div>
    )
}

interface RatingDist {
    '5': number;
    '4': number;
    '3': number;
    '2': number;
    '1': number;
}
